import { link, svgIcon } from './kit';
import { createMuteToggle } from './muteButton';

// The landing page's utility footer (D221) — extracted from homeScreen so that file stays
// inside the ~400-line house limit once the hero became a whole landing page.
//
// Two of its controls are here in spite of the handoff rather than because of it. Its footer
// spec is "settler-name pill → sign-up" and four links, which would quietly delete the only
// way a guest can NAME themselves, and the master mute H12 shipped. A handoff is a visual
// language, not a licence to remove features (D152) — so the chip keeps its inline edit and
// signing in is its own separate link beside it.

export interface UtilityBarOpts {
  doc: Document;
  playerName: { get(): string; set(v: string): void };
  signedIn(): boolean;
  onSignIn(): void;
  onFeedback(): void;
  onSettings?(): void;
  onFriends?(): void;
  /**
   * D248: the operator's dashboard. `visible` is re-read on every refresh because the
   * answer arrives with the account — a page that boots signed in learns it a moment after
   * paint, and a sign-out has to take the entry away again. The client is only DECIDING
   * WHETHER TO DRAW: the route re-derives the permission from the bearer token, so a
   * fabricated flag buys a panel full of 403s.
   */
  admin?: { visible(): boolean; open(): void };
  /** D250/A2: the How to play link was used. The anchor still navigates — this only reports. */
  onHowTo?(): void;
  audio?: {
    isMuted(): boolean;
    setMuted(muted: boolean): void;
    onMuteChange(handler: () => void): () => void;
  };
}

export interface UtilityBar {
  readonly root: HTMLElement;
  /** Re-read the name and the auth zone — called on every return to the menu. */
  refresh(): void;
  setFriendsBadge(n: number): void;
}

export function createUtilityBar(opts: UtilityBarOpts): UtilityBar {
  const d = opts.doc;
  const root = d.createElement('div');
  root.className = 'kolony-utility';
  const left = d.createElement('div');
  left.className = 'kolony-utility-left';
  const right = d.createElement('div');
  right.className = 'kolony-utility-right';

  // ── the name chip, still inline-editable ────────────────────────────────────
  const chip = d.createElement('div');
  chip.className = 'kolony-chip';
  chip.tabIndex = 0;
  chip.setAttribute('role', 'button');
  chip.setAttribute('aria-label', 'Edit your display name');
  const nameSpan = d.createElement('span');
  nameSpan.className = 'kolony-chip-name';
  chip.append(svgIcon(d, 'person', 14), nameSpan, svgIcon(d, 'pencil', 12));
  let editing = false;
  const beginEdit = (): void => {
    if (editing) return;
    editing = true;
    const input = d.createElement('input');
    input.maxLength = 20;
    input.value = opts.playerName.get();
    const done = (commit: boolean): void => {
      if (!editing) return;
      editing = false;
      if (commit) {
        const v = input.value.trim().slice(0, 20);
        if (v) opts.playerName.set(v); // empty ⇒ keep the old name
      }
      nameSpan.textContent = opts.playerName.get();
      input.replaceWith(nameSpan);
    };
    input.addEventListener('keydown', (e) => {
      // stopPropagation: a bubbling Enter/Escape would reach the chip's own handler AFTER
      // done() clears `editing`, and instantly re-open the edit just committed.
      if (e.key === 'Enter') {
        e.stopPropagation();
        done(true);
      } else if (e.key === 'Escape') {
        e.stopPropagation();
        done(false);
      }
    });
    input.addEventListener('blur', () => done(true));
    nameSpan.replaceWith(input);
    input.focus();
    input.select();
  };
  chip.addEventListener('click', beginEdit);
  chip.addEventListener('keydown', (e) => {
    if (!editing && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      beginEdit();
    }
  });

  const auth = d.createElement('div');
  auth.className = 'kolony-home-auth';
  left.append(chip, auth);
  const adminZone = d.createElement('span');
  adminZone.className = 'kolony-admin-zone';
  left.append(adminZone);

  // D212/D218: real anchors to static files — the legal pages and the rules have to outlive
  // an app that will not boot, which is exactly when somebody reaches for them.
  const legalLink = (label: string, href: string): HTMLAnchorElement => {
    const a = d.createElement('a');
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = label;
    return a;
  };
  const legal = d.createElement('span');
  legal.className = 'kolony-legal-links';
  const dot = d.createElement('span');
  dot.textContent = '·';
  legal.append(legalLink('Privacy', '/legal/privacy.html'), dot, legalLink('Terms', '/legal/terms.html'));
  left.append(legal);

  const howTo = legalLink('How to play', '/legal/how-to-play.html');
  howTo.className = 'kolony-util-link';
  howTo.dataset['homeHowTo'] = '1';
  if (opts.onHowTo) howTo.addEventListener('click', () => opts.onHowTo?.());
  right.append(howTo);

  const friendsBadge = d.createElement('span');
  friendsBadge.className = 'kolony-badge';
  friendsBadge.dataset['friendsBadge'] = '1';
  friendsBadge.style.display = 'none';
  if (opts.onFriends) {
    const friendsLink = link(d, 'Friends', () => opts.onFriends?.());
    friendsLink.dataset['homeFriends'] = '1';
    friendsLink.classList.add('kolony-util-link');
    right.append(friendsLink, friendsBadge);
  }
  const feedbackLink = link(d, 'Feedback', () => opts.onFeedback(), { muted: true });
  feedbackLink.classList.add('kolony-util-link');
  right.append(feedbackLink);
  if (opts.onSettings) {
    const onSettings = opts.onSettings;
    const settingsBtn = d.createElement('button');
    settingsBtn.type = 'button';
    settingsBtn.className = 'menu-settings-btn';
    settingsBtn.dataset['homeSettings'] = '1';
    settingsBtn.textContent = '⚙ Settings';
    settingsBtn.addEventListener('click', () => onSettings());
    right.append(settingsBtn);
  }
  if (opts.audio) {
    const audio = opts.audio;
    const toggle = createMuteToggle(d, { isMuted: () => audio.isMuted(), setMuted: (m) => audio.setMuted(m), variant: 'bar' });
    right.append(toggle.el);
    audio.onMuteChange(() => toggle.sync()); // the break menu's copy moves this one
  }
  root.append(left, right);

  // A sign-out during an in-flight refresh must not resurrect the signed-in zone.
  let authGen = 0;
  const rebuildAuth = (): void => {
    void ++authGen;
    auth.replaceChildren();
    const label = opts.signedIn() ? 'Account' : 'Sign in';
    const a = link(d, label, () => opts.onSignIn(), ...(opts.signedIn() ? [] : [{ muted: true }]));
    a.classList.add('kolony-util-link');
    a.dataset['homeAuth'] = '1';
    auth.append(a);
  };

  const rebuildAdmin = (): void => {
    adminZone.replaceChildren();
    const admin = opts.admin;
    if (!admin?.visible()) return; // no flag, no entry — nothing to mis-tap and nothing to explain
    const a = link(d, 'Dashboard', () => admin.open());
    a.classList.add('kolony-util-link');
    a.dataset['homeAdmin'] = '1';
    adminZone.append(a);
  };

  return {
    root,
    refresh(): void {
      nameSpan.textContent = opts.playerName.get();
      rebuildAuth();
      rebuildAdmin();
    },
    setFriendsBadge(n: number): void {
      friendsBadge.textContent = n > 0 ? String(n) : '';
      friendsBadge.style.display = n > 0 ? '' : 'none';
    },
  };
}
